import { forwardRef } from "react";
import LinkWrapper from "../link-wrapper/link-wrapper";
import styles from "./button.module.less";
import { MouseEvent } from "react";

type TButtonSize = "small" | "big";
type TButtonDecoration = "underline" | "none";

type TProps = {
  className?: string;
  size?: TButtonSize;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  label?: string;
  iconRight?: string;
  iconLeft?: string;
  ariaLabel?: string;
};

type TButtonProps = TProps & {
  onClick?: () => void;
  isLoading?: boolean;
};

type TButtonFakeProps = Omit<TButtonProps, "onClick">;

type TButtonLinkProps = TProps & {
  openInNewTab?: boolean;
  url: string;
  decoration?: TButtonDecoration;
  onPreNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

type TIconButtonProps = {
  className?: string;
  icon?: string;
  isDisabled?: boolean;
  ariaLabel?: string;
  onClick?: () => void;
};

type TCommonClassProps = {
  size: TButtonSize;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
};

type TContentProps = {
  label?: string;
  iconRight?: string;
  iconLeft?: string;
  isFullWidth?: boolean;
};

/**
 * Preem button with variations - Primary & Secondary with possibility for icons
 */

/* Primary */

export const PrimaryButton = forwardRef<HTMLButtonElement, TButtonProps>(({ className, ...rest }, forwardedRef) => {
  return <Button ref={forwardedRef} className={[styles.primary, className ?? ""].join(" ")} {...rest} />;
});

PrimaryButton.displayName = "PrimaryButton";

export const PrimaryButtonLink = ({ className, ...rest }: TButtonLinkProps) => {
  return <ButtonLink className={[styles.primary, className ?? ""].join(" ")} {...rest} />;
};

export const PrimaryButtonFake = ({ className, ...rest }: TButtonFakeProps) => {
  return <ButtonFake className={[styles.primary, className ?? ""].join(" ")} {...rest} />;
};

/* Secondary */

export const SecondaryButton = forwardRef<HTMLButtonElement, TButtonProps>(({ className, ...rest }, forwardedRef) => {
  return <Button ref={forwardedRef} className={[styles.secondary, className ?? ""].join(" ")} {...rest} />;
});

SecondaryButton.displayName = "SecondaryButton";

export const SecondaryButtonLink = ({ className, ...rest }: TButtonLinkProps) => {
  return <ButtonLink className={[styles.secondary, className ?? ""].join(" ")} {...rest} />;
};

export const SecondaryButtonFake = ({ className, ...rest }: TButtonFakeProps) => {
  return <ButtonFake className={[styles.secondary, className ?? ""].join(" ")} {...rest} />;
};

/* Outline */

export const OutlineButton = forwardRef<HTMLButtonElement, TButtonProps>(({ className, ...rest }, forwardedRef) => {
  return <Button ref={forwardedRef} className={[styles.outline, className ?? ""].join(" ")} {...rest} />;
});

OutlineButton.displayName = "OutlineButton";

export const OutlineButtonLink = ({ className, ...rest }: TButtonLinkProps) => {
  return <ButtonLink className={[styles.outline, className ?? ""].join(" ")} {...rest} />;
};

export const OutlineButtonFake = ({ className, ...rest }: TButtonFakeProps) => {
  return <ButtonFake className={[styles.outline, className ?? ""].join(" ")} {...rest} />;
};

/* Icon buttons */

export const PrimaryIconButton = ({ className, icon, isDisabled, ariaLabel, onClick }: TIconButtonProps) => {
  return (
    <Button
      className={[styles.iconButton, styles.primary, className ?? ""].join(" ")}
      onClick={onClick}
      iconRight={icon}
      isDisabled={isDisabled}
      ariaLabel={ariaLabel}
    />
  );
};

export const SecondaryIconButton = ({ className, icon, isDisabled, ariaLabel, onClick }: TIconButtonProps) => {
  return (
    <Button
      className={[styles.iconButton, styles.secondary, className ?? ""].join(" ")}
      onClick={onClick}
      iconRight={icon}
      isDisabled={isDisabled}
      ariaLabel={ariaLabel}
    />
  );
};

export const OutlineIconButton = ({ className, icon, isDisabled, ariaLabel, onClick }: TIconButtonProps) => {
  return (
    <Button
      className={[styles.iconButton, styles.outline, className ?? ""].join(" ")}
      onClick={onClick}
      iconRight={icon}
      isDisabled={isDisabled}
      ariaLabel={ariaLabel}
    />
  );
};

export const UnstyledButton = ({ className, ...rest }: TButtonProps) => {
  return <Button className={[styles.unstyled, className ?? ""].join(" ")} {...rest} />;
};

export const UnstyledButtonLink = ({ className, ...rest }: TButtonLinkProps) => {
  return <ButtonLink className={[styles.unstyled, className ?? ""].join(" ")} {...rest} />;
};

const getCommonClasses = ({ size, isFullWidth, isDisabled, isLoading }: TCommonClassProps): string => {
  return [
    styles.common,
    styles[size],
    isFullWidth ? styles.fullWidth : "",
    isDisabled && !isLoading ? styles.disabled : "",
    isLoading ? styles.loading : "",
  ].join(" ");
};

const Content = ({ iconRight, iconLeft, label, isFullWidth }: TContentProps) => {
  const iconOnly = !label;

  return (
    <div
      className={[
        styles.labelWrapper,
        iconRight && !iconOnly ? styles.paddedIconRight : "",
        iconLeft && !iconOnly ? styles.paddedIconLeft : "",
        iconOnly ? styles.noPadding : "",
        isFullWidth ? styles.labelWrapperFullWidth : "",
      ].join(" ")}
    >
      {iconLeft && <span className={`${styles.icon} ${iconOnly ? "" : styles.iconLeft} ${iconLeft}`} />}
      <div className={styles.buttonText}>{label}</div>
      {iconRight && <span className={`${styles.icon} ${iconOnly ? "" : styles.iconRight} ${iconRight}`} />}
    </div>
  );
};

export const ButtonLink = ({
  className,
  size = "big",
  isFullWidth,
  isDisabled,
  label,
  iconLeft,
  iconRight,
  openInNewTab,
  url,
  decoration = "underline",
  onPreNavigate,
}: TButtonLinkProps) => {
  const commonClasses = getCommonClasses({ size, isFullWidth, isDisabled });
  return (
    <LinkWrapper
      onPreNavigate={onPreNavigate}
      className={[
        styles.link,
        className ?? "",
        commonClasses,
        decoration === "underline" ? styles.underlined : "",
      ].join(" ")}
      target={openInNewTab ? "_blank" : undefined}
      url={url}
      rel="noreferrer"
    >
      <Content iconLeft={iconLeft} iconRight={iconRight} label={label} isFullWidth={isFullWidth} />
    </LinkWrapper>
  );
};

export const ButtonFake = ({
  className,
  size = "big",
  isFullWidth,
  label,
  iconLeft,
  iconRight,
  isDisabled,
  isLoading,
}: TButtonFakeProps) => {
  const commonClasses = getCommonClasses({ size, isFullWidth, isDisabled, isLoading });

  return (
    <div className={[styles.button, styles.fake, className ?? "", commonClasses].join(" ")}>
      <Content
        iconLeft={isLoading ? "ui-icon-loading" : iconLeft}
        iconRight={isLoading ? undefined : iconRight}
        label={label}
        isFullWidth={isFullWidth}
      />
    </div>
  );
};

export const Button = forwardRef<HTMLButtonElement, TButtonProps>(
  (
    { className, onClick, size = "big", isFullWidth, isDisabled, label, iconLeft, iconRight, isLoading, ariaLabel },
    forwardedRef,
  ) => {
    const commonClasses = getCommonClasses({ size, isFullWidth, isDisabled, isLoading });

    return (
      <button
        ref={forwardedRef}
        type={"button"}
        className={[styles.button, className ?? "", commonClasses].join(" ")}
        onClick={onClick}
        disabled={isDisabled || isLoading}
        aria-label={ariaLabel}
      >
        <Content
          iconLeft={isLoading ? "ui-icon-loading" : iconLeft}
          iconRight={isLoading ? undefined : iconRight}
          label={label}
          isFullWidth={isFullWidth}
        />
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
